import React from 'react'

/** Default structured ECDD answers — persisted under buckets_snapshot.formAnswers */
export const DEFAULT_ECDD_FORM_ANSWERS = {
  triggerType: '',
  linkedAlertId: '',
  observer: '',
  obsDate: '',
  priorSmr: '',
  triggerDesc: '',
  triggerDate: '',
  kycCurrent: '',
  docType: '',
  docNum: '',
  docExp: '',
  verMethod: '',
  verResult: '',
  verDate: '',
  occupation: '',
  occConsistent: '',
  customerType: '',
  sofRequired: '',
  sofNoReason: '',
  sofMethod: '',
  sofDate: '',
  sofBy: '',
  sofCat: '',
  sofOtherDetail: '',
  sowCat: '',
  sofEvidence: '',
  sofResp: '',
  sofAssess: '',
  satOverride: '',
  actFrom: '',
  actTo: '',
  actSummary: '',
  actConsistent: '',
  inconNotes: '',
  indicators: [],
  engaged: '',
  engDate: '',
  engBy: '',
  engMode: '',
  tipDecl: false,
  tipDeclPostSmr: false,
  scrDate: '',
  scrResult: '',
  scrRef: '',
  scrDisp: '',
  pepClass: '',
  coFindings: '',
  nextReview: '',
  cadence: '',
  newRisk: '',
}

const INDICATORS = [
  { value: 'structuring', label: 'Structuring below thresholds', hint: 'Splitting transactions to avoid reporting triggers' },
  { value: 'minimal-play', label: 'Minimal play / cash-through', hint: 'Large insertions with little gaming before collect' },
  { value: 'sof-mismatch', label: 'Activity inconsistent with profile', hint: 'Spend inconsistent with occupation or SoF' },
  { value: 'refusal', label: 'Reluctance / refusal to provide information', hint: 'Evasive about identity, SoF or activity' },
  { value: 'thirdparty', label: 'Third-party involvement', hint: 'Playing or collecting on behalf of another' },
  { value: 'staff-influence', label: 'Attempts to influence staff', hint: 'Enquiries about reporting, tips/incentives' },
]

function hasVal(v) {
  return v != null && String(v).trim() !== ''
}

export function isForeignPep(a) {
  return a.scrResult === 'hit' && a.scrDisp === 'true' && a.pepClass === 'foreign'
}

export function isPepPath(a) {
  return (
    a.triggerType === 'pep' ||
    (a.scrResult === 'hit' && a.scrDisp === 'true' && a.pepClass && a.pepClass !== 'notpep')
  )
}

export function occupationFlagged(a) {
  return ['student', 'unemployed', 'cash'].includes(a.occupation)
}

/** Completeness gates for sign-off — mirrors PaySafeGo_ECDD_Form_Prototype.html */
export function evaluateEcddFormGates(a) {
  const t = a.triggerType
  const foreignPep = isForeignPep(a)
  const sofReq = foreignPep ? 'yes' : a.sofRequired
  const actMandatory = t === 'alert' || t === 'unusual' || t === 'risk'
  const inconsistent = a.actConsistent === 'no'
  const indicatorsSelected = Array.isArray(a.indicators) && a.indicators.length > 0
  const refused = a.sofResp === 'refused'
  const postSmr = t === 'postsmr'
  const hit = a.scrResult === 'hit'
  const occFlag = occupationFlagged(a)

  const triggerOk =
    !!t &&
    hasVal(a.triggerDesc) &&
    hasVal(a.triggerDate) &&
    (t !== 'alert' || hasVal(a.linkedAlertId)) &&
    (t !== 'manual' || (hasVal(a.observer) && hasVal(a.obsDate))) &&
    (t !== 'postsmr' || hasVal(a.priorSmr))

  const kycOk =
    !!a.kycCurrent &&
    (a.kycCurrent !== 'no' ||
      (hasVal(a.docType) &&
        hasVal(a.docNum) &&
        hasVal(a.docExp) &&
        hasVal(a.verMethod) &&
        hasVal(a.verResult) &&
        hasVal(a.verDate))) &&
    (!occFlag || !!a.occConsistent)

  let sofOk = false
  if (sofReq === 'no') sofOk = hasVal(a.sofNoReason)
  else if (sofReq === 'yes') {
    sofOk =
      hasVal(a.sofMethod) &&
      hasVal(a.sofDate) &&
      hasVal(a.sofBy) &&
      hasVal(a.sofCat) &&
      (a.sofCat !== 'other' || hasVal(a.sofOtherDetail)) &&
      !!a.sofResp &&
      !!a.sofAssess &&
      (!(refused && a.sofAssess === 'sat') || hasVal(a.satOverride))
  }

  const activityOk =
    !actMandatory && !inconsistent
      ? true
      : hasVal(a.actSummary) && (!inconsistent || (hasVal(a.inconNotes) && indicatorsSelected))

  let engOk = false
  if (a.engaged === 'yes') {
    engOk = hasVal(a.engDate) && hasVal(a.engBy) && hasVal(a.engMode) && !!a.tipDecl
  } else if (a.engaged === 'no') engOk = true
  if (postSmr) engOk = engOk && !!a.tipDeclPostSmr

  const screenOk =
    hasVal(a.scrDate) &&
    !!a.scrResult &&
    (!hit || (!!a.scrDisp && (a.scrDisp !== 'true' || !!a.pepClass)))

  const gates = [
    { id: 'trigger', label: 'Trigger recorded (type, description, date)', ok: triggerOk },
    {
      id: 'kyc',
      label: 'KYC status answered' + (a.kycCurrent === 'no' ? ' + re-verification complete' : ''),
      ok: kycOk,
    },
    { id: 'sof', label: 'SoF/SoW enquiry resolved (done, or non-requirement documented)', ok: sofOk },
  ]
  if (actMandatory || inconsistent) {
    gates.push({ id: 'activity', label: 'Activity analysis complete', ok: activityOk })
  }
  gates.push({
    id: 'engage',
    label: 'Engagement answered' + (a.engaged === 'yes' || postSmr ? ' + tipping-off declared' : ''),
    ok: engOk,
  })
  gates.push({
    id: 'screen',
    label: 'Screening refresh recorded' + (hit ? ' + match dispositioned' : ''),
    ok: screenOk,
  })

  return {
    gates,
    allOk: gates.every((g) => g.ok),
    sofReq,
    foreignPep,
    actMandatory,
    inconsistent,
    indicatorsSelected,
    refused,
    postSmr,
    hit,
    occFlag,
    returnBlocked: sofReq === 'yes' && (a.sofAssess === 'unsat' || a.sofAssess === 'further'),
  }
}

/** Payload for POST /api/ecdd-approval-triggers/check from form + decision */
export function formAnswersToTriggerCheck(a, decision, riskScore) {
  const pepClass = a.pepClass || (a.triggerType === 'pep' ? 'foreign' : '')
  return {
    decision: decision || null,
    pep_type: pepClass === 'foreign' || pepClass === 'domestic' || pepClass === 'intorg' ? pepClass : null,
    sanctions_match: a.scrResult === 'hit' && a.scrDisp === 'true' && a.pepClass === 'notpep',
    is_pep: isPepPath(a),
    risk_score: riskScore != null ? Number(riskScore) : null,
    cdd_tier_change: null,
  }
}

function Chip({ name, value, checked, onChange, label, disabled, danger }) {
  return (
    <label
      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border text-[13px] font-medium cursor-pointer select-none transition-colors ${
        disabled ? 'opacity-40 cursor-not-allowed' : ''
      } ${
        checked
          ? danger
            ? 'border-red-500 bg-red-50 text-red-800 font-semibold'
            : 'border-psg-teal bg-[#0A516908] text-psg-teal font-semibold'
          : 'border-slate-200 bg-white text-slate-600'
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => !disabled && onChange(value)}
        className="accent-[#0A5169] m-0"
      />
      {label}
    </label>
  )
}

function Field({ label, required, hint, children }) {
  return (
    <div className="mb-4 last:mb-0">
      <label className="block text-[13px] font-semibold text-slate-600 mb-1.5">
        {label}
        {required ? <span className="text-red-600"> *</span> : null}
      </label>
      {children}
      {hint ? <p className="text-xs text-slate-400 mt-1 mb-0">{hint}</p> : null}
    </div>
  )
}

const inputClass =
  'w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:border-psg-teal disabled:bg-slate-100 disabled:text-slate-400'

function Cond({ open, children }) {
  if (!open) return null
  return (
    <div className="border-l-[3px] border-psg-teal pl-4 pr-2 py-3 my-3 bg-[#0A516905] rounded-r-lg">
      {children}
    </div>
  )
}

function Notice({ kind = 'info', children }) {
  const styles = {
    info: 'bg-[#0A516908] text-psg-teal border-[#0A516925]',
    warn: 'bg-amber-50 text-amber-800 border-amber-200',
    block: 'bg-red-50 text-red-700 border-red-200',
  }
  return (
    <div className={`rounded-lg px-3.5 py-2.5 text-[13px] font-medium border mb-3 ${styles[kind] || styles.info}`}>
      {children}
    </div>
  )
}

/**
 * Structured ECDD form sections (trigger → screening) + shared field helpers.
 * Parent owns formAnswers state and FormSection wrappers for numbering.
 */
export function EcddFormFields({ formAnswers: a, onChange, readOnly = false }) {
  const set = (patch) => {
    if (readOnly) return
    onChange(patch)
  }
  const setField = (key, value) => set({ [key]: value })
  const evalState = evaluateEcddFormGates(a)
  const {
    sofReq,
    foreignPep,
    actMandatory,
    inconsistent,
    indicatorsSelected,
    refused,
    postSmr,
    hit,
    occFlag,
  } = evalState

  const toggleIndicator = (value) => {
    if (readOnly) return
    const cur = Array.isArray(a.indicators) ? a.indicators : []
    setField(
      'indicators',
      cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value]
    )
  }

  return (
    <div className="space-y-1" data-ecdd-form-eval={JSON.stringify({ sofReq, foreignPep })}>
      {/* Trigger fields — rendered inside parent FormSection */}
      <div data-section="trigger">
        <Field label="Trigger type" required>
          <div className="flex flex-wrap gap-2">
            {[
              ['alert', 'Rule / alert'],
              ['pep', 'PEP / screening hit'],
              ['risk', 'High risk rating'],
              ['manual', 'Manual observation'],
              ['postsmr', 'Post-SMR'],
              ['unusual', 'Unusual / complex activity'],
            ].map(([v, l]) => (
              <Chip
                key={v}
                name="trig"
                value={v}
                label={l}
                checked={a.triggerType === v}
                disabled={readOnly}
                onChange={(val) => setField('triggerType', val)}
              />
            ))}
          </div>
        </Field>
        <Cond open={a.triggerType === 'alert'}>
          <Field label="Linked alert ID" required>
            <input className={inputClass} disabled={readOnly} value={a.linkedAlertId} onChange={(e) => setField('linkedAlertId', e.target.value)} placeholder="e.g. ALT-2026-0412" />
          </Field>
        </Cond>
        <Cond open={a.triggerType === 'manual'}>
          <Field label="Observer name & role" required>
            <input className={inputClass} disabled={readOnly} value={a.observer} onChange={(e) => setField('observer', e.target.value)} placeholder="Name — role" />
          </Field>
          <Field label="Observation date" required>
            <input type="date" className={`${inputClass} max-w-[220px]`} disabled={readOnly} value={a.obsDate} onChange={(e) => setField('obsDate', e.target.value)} />
          </Field>
        </Cond>
        <Cond open={a.triggerType === 'postsmr'}>
          <Field label="Prior SMR reference" required>
            <input className={inputClass} disabled={readOnly} value={a.priorSmr} onChange={(e) => setField('priorSmr', e.target.value)} placeholder="SMR-YYYY-NNNN" />
          </Field>
        </Cond>
        <Field label="Trigger description" required hint="Locked narrative of why ECDD was opened.">
          <textarea className={`${inputClass} min-h-[72px] resize-y`} disabled={readOnly} value={a.triggerDesc} onChange={(e) => setField('triggerDesc', e.target.value)} placeholder="Describe what triggered this ECDD." />
        </Field>
        <Field label="Trigger date" required>
          <input type="date" className={`${inputClass} max-w-[220px]`} disabled={readOnly} value={a.triggerDate} onChange={(e) => setField('triggerDate', e.target.value)} />
        </Field>
      </div>
    </div>
  )
}

/** Section bodies split for parent numbering */
export function EcddTriggerFields(props) {
  return <EcddFormFields {...props} />
}

export function EcddKycFields({ formAnswers: a, onChange, readOnly }) {
  const setField = (key, value) => {
    if (readOnly) return
    onChange({ [key]: value })
  }
  const occFlag = occupationFlagged(a)
  return (
    <>
      <Field label="Is the patron's KYC current?" required>
        <div className="flex flex-wrap gap-2">
          <Chip name="kyc" value="yes" label="Yes" checked={a.kycCurrent === 'yes'} disabled={readOnly} onChange={(v) => setField('kycCurrent', v)} />
          <Chip name="kyc" value="no" label="No" checked={a.kycCurrent === 'no'} disabled={readOnly} onChange={(v) => setField('kycCurrent', v)} />
        </div>
      </Field>
      <Cond open={a.kycCurrent === 'no'}>
        <Field label="Document type" required>
          <select className={inputClass} disabled={readOnly} value={a.docType} onChange={(e) => setField('docType', e.target.value)}>
            <option value="">Select…</option>
            <option>Driver licence</option>
            <option>Passport</option>
            <option>Photo card</option>
            <option>Other</option>
          </select>
        </Field>
        <Field label="Document number" required>
          <input className={inputClass} disabled={readOnly} value={a.docNum} onChange={(e) => setField('docNum', e.target.value)} />
        </Field>
        <Field label="Expiry" required>
          <input type="date" className={`${inputClass} max-w-[220px]`} disabled={readOnly} value={a.docExp} onChange={(e) => setField('docExp', e.target.value)} />
        </Field>
        <Field label="Verification method" required>
          <select className={inputClass} disabled={readOnly} value={a.verMethod} onChange={(e) => setField('verMethod', e.target.value)}>
            <option value="">Select…</option>
            <option>Sight-and-record (in person)</option>
            <option>Electronic verification (DVS)</option>
          </select>
        </Field>
        <Field label="Result" required>
          <select className={inputClass} disabled={readOnly} value={a.verResult} onChange={(e) => setField('verResult', e.target.value)}>
            <option value="">Select…</option>
            <option>Verified</option>
            <option>Not verified</option>
          </select>
        </Field>
        <Field label="Verification date" required hint="Structured details only — no document images retained.">
          <input type="date" className={`${inputClass} max-w-[220px]`} disabled={readOnly} value={a.verDate} onChange={(e) => setField('verDate', e.target.value)} />
        </Field>
      </Cond>
      <Field label="Occupation / employment status">
        <select className={inputClass} disabled={readOnly} value={a.occupation} onChange={(e) => setField('occupation', e.target.value)}>
          <option value="">Select…</option>
          <option value="employed">Employed — salaried</option>
          <option value="selfemployed">Self-employed / business owner</option>
          <option value="cash">Cash-intensive business</option>
          <option value="student">Student</option>
          <option value="unemployed">Unemployed</option>
          <option value="retired">Retired</option>
          <option value="other">Other</option>
        </select>
      </Field>
      {occFlag && (
        <Notice kind="info">
          Occupation flagged by AUSTRAC guidance — confirm whether observed activity is consistent. SoF/SoW enquiry is <b>recommended</b>.
        </Notice>
      )}
      <Cond open={occFlag}>
        <Field label="Is activity consistent with stated occupation?" required>
          <div className="flex flex-wrap gap-2">
            <Chip name="occCons" value="yes" label="Yes" checked={a.occConsistent === 'yes'} disabled={readOnly} onChange={(v) => setField('occConsistent', v)} />
            <Chip name="occCons" value="no" label="No" checked={a.occConsistent === 'no'} disabled={readOnly} onChange={(v) => setField('occConsistent', v)} />
          </div>
        </Field>
      </Cond>
      <Field label="Customer type" hint="Feeds residual risk rating fields on the profile.">
        <select className={inputClass} disabled={readOnly} value={a.customerType} onChange={(e) => setField('customerType', e.target.value)}>
          <option value="">Select…</option>
          <option>Member — local</option>
          <option>Member — visiting</option>
          <option>Non-member / guest</option>
          <option>Temporary member</option>
        </select>
      </Field>
    </>
  )
}

export function EcddSofFields({ formAnswers: a, onChange, readOnly }) {
  const setField = (key, value) => {
    if (readOnly) return
    onChange({ [key]: value })
  }
  const foreignPep = isForeignPep(a)
  const sofReq = foreignPep ? 'yes' : a.sofRequired
  const refused = a.sofResp === 'refused'
  return (
    <>
      {foreignPep && (
        <Notice kind="block">Foreign PEP identified — SoF/SoW enquiry is <b>mandatory</b> and cannot be skipped.</Notice>
      )}
      <Field label="Is a SoF/SoW enquiry required for this case?" required hint="Mandatory for foreign PEPs. Risk-based otherwise.">
        <div className="flex flex-wrap gap-2">
          <Chip name="sofReq" value="yes" label="Yes" checked={sofReq === 'yes'} disabled={readOnly} onChange={(v) => setField('sofRequired', v)} />
          <Chip name="sofReq" value="no" label="No" checked={sofReq === 'no'} disabled={readOnly || foreignPep} onChange={(v) => setField('sofRequired', v)} />
        </div>
      </Field>
      <Cond open={sofReq === 'no'}>
        <Field label="Reason enquiry not required" required>
          <textarea className={`${inputClass} min-h-[72px] resize-y`} disabled={readOnly} value={a.sofNoReason} onChange={(e) => setField('sofNoReason', e.target.value)} placeholder="Document why SoF/SoW is not warranted — absence must be a recorded decision." />
        </Field>
      </Cond>
      <Cond open={sofReq === 'yes'}>
        <Field label="Enquiry method" required>
          <select className={inputClass} disabled={readOnly} value={a.sofMethod} onChange={(e) => setField('sofMethod', e.target.value)}>
            <option value="">Select…</option>
            <option>Conversation</option>
            <option>Written request</option>
            <option>Document request</option>
          </select>
        </Field>
        <Field label="Enquiry date" required>
          <input type="date" className={`${inputClass} max-w-[220px]`} disabled={readOnly} value={a.sofDate} onChange={(e) => setField('sofDate', e.target.value)} />
        </Field>
        <Field label="Enquiry made by" required>
          <input className={inputClass} disabled={readOnly} value={a.sofBy} onChange={(e) => setField('sofBy', e.target.value)} placeholder="Name — role" />
        </Field>
        <Field label="Stated source of funds" required>
          <select className={inputClass} disabled={readOnly} value={a.sofCat} onChange={(e) => setField('sofCat', e.target.value)}>
            <option value="">Select…</option>
            <option>Salary / wages</option>
            <option>Business income</option>
            <option>Savings</option>
            <option>Gambling winnings</option>
            <option>Asset sale</option>
            <option>Inheritance</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Cond open={a.sofCat === 'other'}>
          <Field label="Detail" required>
            <input className={inputClass} disabled={readOnly} value={a.sofOtherDetail} onChange={(e) => setField('sofOtherDetail', e.target.value)} />
          </Field>
        </Cond>
        <Field label="Stated source of wealth">
          <select className={inputClass} disabled={readOnly} value={a.sowCat} onChange={(e) => setField('sowCat', e.target.value)}>
            <option value="">Select…</option>
            <option>Salary / wages</option>
            <option>Business income</option>
            <option>Savings</option>
            <option>Gambling winnings</option>
            <option>Asset sale</option>
            <option>Inheritance</option>
            <option>Other</option>
          </select>
        </Field>
        <Field label="Evidence obtained">
          <textarea className={`${inputClass} min-h-[64px] resize-y`} disabled={readOnly} value={a.sofEvidence} onChange={(e) => setField('sofEvidence', e.target.value)} placeholder="Structured description of evidence sighted." />
        </Field>
        <Field label="Patron response" required>
          <div className="flex flex-wrap gap-2">
            {[
              ['coop', 'Cooperative'],
              ['partial', 'Partial'],
              ['refused', 'Refused / evasive'],
            ].map(([v, l]) => (
              <Chip key={v} name="sofResp" value={v} label={l} checked={a.sofResp === v} disabled={readOnly} onChange={(val) => setField('sofResp', val)} />
            ))}
          </div>
        </Field>
        {refused && (
          <Notice kind="warn">Refusal or evasiveness is itself an indicator. A &quot;Satisfactory&quot; assessment requires an override justification.</Notice>
        )}
        <Field label="CO assessment" required>
          <div className="flex flex-wrap gap-2">
            {[
              ['sat', 'Satisfactory'],
              ['unsat', 'Unsatisfactory'],
              ['further', 'Requires further enquiry'],
            ].map(([v, l]) => (
              <Chip key={v} name="sofAssess" value={v} label={l} checked={a.sofAssess === v} disabled={readOnly} onChange={(val) => setField('sofAssess', val)} />
            ))}
          </div>
        </Field>
        <Cond open={refused && a.sofAssess === 'sat'}>
          <Field label="Override justification" required>
            <textarea className={`${inputClass} min-h-[64px] resize-y`} disabled={readOnly} value={a.satOverride} onChange={(e) => setField('satOverride', e.target.value)} placeholder="Explain why Satisfactory despite refused/evasive response." />
          </Field>
        </Cond>
      </Cond>
    </>
  )
}

export function EcddActivityFields({ formAnswers: a, onChange, readOnly }) {
  const setField = (key, value) => {
    if (readOnly) return
    onChange({ [key]: value })
  }
  const { actMandatory, inconsistent, indicatorsSelected } = evaluateEcddFormGates(a)
  const toggleIndicator = (value) => {
    if (readOnly) return
    const cur = Array.isArray(a.indicators) ? a.indicators : []
    setField(
      'indicators',
      cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value]
    )
  }
  return (
    <>
      <Field label="Review period">
        <div className="flex flex-wrap gap-2">
          <input type="date" className={`${inputClass} max-w-[200px]`} disabled={readOnly} value={a.actFrom} onChange={(e) => setField('actFrom', e.target.value)} />
          <input type="date" className={`${inputClass} max-w-[200px]`} disabled={readOnly} value={a.actTo} onChange={(e) => setField('actTo', e.target.value)} />
        </div>
      </Field>
      <Field label="Summary of activity reviewed" required={actMandatory}>
        <textarea className={`${inputClass} min-h-[72px] resize-y`} disabled={readOnly} value={a.actSummary} onChange={(e) => setField('actSummary', e.target.value)} placeholder="Turnover, payout pattern, session behaviour." />
      </Field>
      <Field label="Consistent with stated SoF and profile?">
        <div className="flex flex-wrap gap-2">
          <Chip name="actCons" value="yes" label="Yes" checked={a.actConsistent === 'yes'} disabled={readOnly} onChange={(v) => setField('actConsistent', v)} />
          <Chip name="actCons" value="no" label="No" checked={a.actConsistent === 'no'} disabled={readOnly} onChange={(v) => setField('actConsistent', v)} />
        </div>
      </Field>
      <Cond open={inconsistent}>
        <Field label="Inconsistency notes" required>
          <textarea className={`${inputClass} min-h-[64px] resize-y`} disabled={readOnly} value={a.inconNotes} onChange={(e) => setField('inconNotes', e.target.value)} />
        </Field>
      </Cond>
      <Field label="Indicators observed" required={inconsistent}>
        <div className="flex flex-col gap-1.5">
          {INDICATORS.map((ind) => {
            const on = Array.isArray(a.indicators) && a.indicators.includes(ind.value)
            return (
              <label
                key={ind.value}
                className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer ${
                  on ? 'border-psg-teal bg-[#0A516908]' : 'border-slate-200 bg-white'
                } ${readOnly ? 'opacity-60' : ''}`}
              >
                <input
                  type="checkbox"
                  className="accent-[#0A5169] mt-1"
                  checked={on}
                  disabled={readOnly}
                  onChange={() => toggleIndicator(ind.value)}
                />
                <span>
                  <span className="text-[13px] font-medium text-slate-700 block">{ind.label}</span>
                  <span className="text-xs text-slate-400">{ind.hint}</span>
                </span>
              </label>
            )
          })}
        </div>
      </Field>
      {indicatorsSelected && (
        <Notice kind="warn">
          One or more indicators recorded — <b>Escalate → SMR</b> may be appropriate in the Decision section. Suspicion formation remains an explicit CO act in the SMR module.
        </Notice>
      )}
    </>
  )
}

export function EcddEngageFields({ formAnswers: a, onChange, readOnly }) {
  const setField = (key, value) => {
    if (readOnly) return
    onChange({ [key]: value })
  }
  const postSmr = a.triggerType === 'postsmr'
  return (
    <>
      <Field label="Was the patron engaged during this ECDD?" required>
        <div className="flex flex-wrap gap-2">
          <Chip name="engaged" value="yes" label="Yes" checked={a.engaged === 'yes'} disabled={readOnly} onChange={(v) => setField('engaged', v)} />
          <Chip name="engaged" value="no" label="No" checked={a.engaged === 'no'} disabled={readOnly} onChange={(v) => setField('engaged', v)} />
        </div>
      </Field>
      <Cond open={a.engaged === 'yes'}>
        <Field label="Engagement date" required>
          <input type="date" className={`${inputClass} max-w-[220px]`} disabled={readOnly} value={a.engDate} onChange={(e) => setField('engDate', e.target.value)} />
        </Field>
        <Field label="Engaged by" required>
          <input className={inputClass} disabled={readOnly} value={a.engBy} onChange={(e) => setField('engBy', e.target.value)} placeholder="Name — role" />
        </Field>
        <Field label="Mode" required>
          <select className={inputClass} disabled={readOnly} value={a.engMode} onChange={(e) => setField('engMode', e.target.value)}>
            <option value="">Select…</option>
            <option>In person</option>
            <option>Phone</option>
            <option>Written</option>
          </select>
        </Field>
        <label className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border mt-2 cursor-pointer ${a.tipDecl ? 'border-psg-teal bg-[#0A516908]' : 'border-slate-200'}`}>
          <input type="checkbox" className="accent-[#0A5169] mt-1" checked={!!a.tipDecl} disabled={readOnly} onChange={(e) => setField('tipDecl', e.target.checked)} />
          <span>
            <span className="text-[13px] font-medium text-slate-700">Tipping-off declaration <span className="text-red-600">*</span></span>
            <span className="text-xs text-slate-400 block mt-0.5">
              I confirm that no disclosure of any suspicion, SMR, or intention to file an SMR was made to the patron or any third party during this engagement.
            </span>
          </span>
        </label>
      </Cond>
      {postSmr && (
        <>
          <Notice kind="block">Post-SMR trigger — tipping-off declaration is mandatory covering all patron contact during the ECDD period.</Notice>
          <label className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer ${a.tipDeclPostSmr ? 'border-psg-teal bg-[#0A516908]' : 'border-slate-200'}`}>
            <input type="checkbox" className="accent-[#0A5169] mt-1" checked={!!a.tipDeclPostSmr} disabled={readOnly} onChange={(e) => setField('tipDeclPostSmr', e.target.checked)} />
            <span>
              <span className="text-[13px] font-medium text-slate-700">Tipping-off declaration (post-SMR) <span className="text-red-600">*</span></span>
              <span className="text-xs text-slate-400 block mt-0.5">
                I confirm that no disclosure relating to the prior SMR or any ongoing suspicion has been made to the patron or any third party.
              </span>
            </span>
          </label>
        </>
      )}
    </>
  )
}

export function EcddScreeningFields({ formAnswers: a, onChange, readOnly }) {
  const setField = (key, value) => {
    if (readOnly) return
    onChange({ [key]: value })
  }
  const hit = a.scrResult === 'hit'
  return (
    <>
      <Field label="Re-screen date" required>
        <input type="date" className={`${inputClass} max-w-[220px]`} disabled={readOnly} value={a.scrDate} onChange={(e) => setField('scrDate', e.target.value)} />
      </Field>
      <Field label="Result" required>
        <div className="flex flex-wrap gap-2">
          <Chip name="scrResult" value="clear" label="Clear" checked={a.scrResult === 'clear'} disabled={readOnly} onChange={(v) => setField('scrResult', v)} />
          <Chip name="scrResult" value="hit" label="Hit" checked={a.scrResult === 'hit'} disabled={readOnly} onChange={(v) => setField('scrResult', v)} />
        </div>
      </Field>
      <Field label="Screening log reference">
        <input className={`${inputClass} max-w-[280px]`} disabled={readOnly} value={a.scrRef} onChange={(e) => setField('scrRef', e.target.value)} placeholder="e.g. SCR-2026-0048" />
      </Field>
      <Cond open={hit}>
        <Field label="Match disposition" required>
          <div className="flex flex-wrap gap-2">
            <Chip name="scrDisp" value="false" label="False positive" checked={a.scrDisp === 'false'} disabled={readOnly} onChange={(v) => setField('scrDisp', v)} />
            <Chip name="scrDisp" value="true" label="True positive" checked={a.scrDisp === 'true'} disabled={readOnly} onChange={(v) => setField('scrDisp', v)} />
          </div>
        </Field>
        <Cond open={a.scrDisp === 'true'}>
          <Field label="PEP classification" required hint="Foreign PEP forces SoF/SoW enquiry and senior management approval via Approval Policies.">
            <div className="flex flex-wrap gap-2">
              {[
                ['domestic', 'Domestic'],
                ['foreign', 'Foreign'],
                ['intorg', 'International organisation'],
                ['notpep', 'Not a PEP (other list)'],
              ].map(([v, l]) => (
                <Chip key={v} name="pepClass" value={v} label={l} checked={a.pepClass === v} disabled={readOnly} onChange={(val) => setField('pepClass', val)} />
              ))}
            </div>
          </Field>
        </Cond>
      </Cond>
    </>
  )
}

export function EcddSignOffGate({ gates, allOk, checklistMandatoryDone, checklistMandatoryTotal }) {
  const checklistOk = checklistMandatoryTotal === 0 || checklistMandatoryDone >= checklistMandatoryTotal
  const rows = [
    ...gates,
    {
      id: 'checklist',
      label: `Supporting measures checklist (${checklistMandatoryDone}/${checklistMandatoryTotal} required)`,
      ok: checklistOk,
    },
  ]
  const ready = allOk && checklistOk
  return (
    <div>
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 mb-3">
        <div className="text-[13px] font-bold text-[#073B4D] mb-2.5">Completeness — sections this trigger made mandatory</div>
        <div className="space-y-1">
          {rows.map((g) => (
            <div
              key={g.id}
              className={`flex items-center gap-2 text-[13px] py-0.5 ${g.ok ? 'text-green-700' : 'text-slate-500'}`}
            >
              <span
                className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                  g.ok ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-600'
                }`}
              >
                {g.ok ? '✓' : '•'}
              </span>
              <span>{g.label}</span>
            </div>
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-400 m-0">
        {ready
          ? 'Ready to submit — procedural completeness met. CO judgment outcome is never blocked by the gate once complete.'
          : 'Complete the incomplete items above before submitting for manager approval. Approval Policies (Governance Safe) still decide who must approve.'}
      </p>
    </div>
  )
}

export function mergeFormAnswers(raw) {
  return { ...DEFAULT_ECDD_FORM_ANSWERS, ...(raw && typeof raw === 'object' ? raw : {}) }
}
